import { useMemo } from 'react';
import { Select, SelectItem } from '@scholastic/mockingjay';
import { LoadingSelect } from '@data/components/basic/LoadingSelect/LoadingSelect';
import { LabeledMultiSelect } from '@data/components/report/ReportFilterBar/LabeledMultiSelect/LabeledMultiSelect';
import { useGetTeachersByOrgApi } from '@data/hooks/api/use-get-teachers-by-org-api/use-get-teachers-by-org-api';
import { sortByLastName } from '@data/utils/sort-by-last-name';

export const ALL_TEACHERS_LABEL = 'All Teachers';

interface TeacherDropdownProps {
  /** Whose educators are offered; without an organization there are none. */
  orgId: string;
  /** Empty means every educator in the organization. */
  selectedTeacherIds: string[];
  onChange: (teacherIds: string[]) => void;
  /** `single` for a report about one educator. */
  mode?: 'multiselect' | 'single';
  /** Fixed to the educator being read, with no choice to make. */
  disabled?: boolean;
}

/** The educators in one organization, for the admin drill-downs. */
export function TeacherDropdown({
  orgId,
  selectedTeacherIds,
  onChange,
  mode = 'multiselect',
  disabled = false,
}: TeacherDropdownProps) {
  const { data: teachers, isLoading } = useGetTeachersByOrgApi(orgId, {
    retry: 0,
  });

  const teacherOptions = useMemo(
    () =>
      [...(teachers ?? [])].sort(sortByLastName).map((teacher) => ({
        value: teacher.userId,
        label: teacher.middleName
          ? `${teacher.lastName}, ${teacher.firstName} ${teacher.middleName}`
          : `${teacher.lastName}, ${teacher.firstName}`,
      })),
    [teachers]
  );

  // Only the drill-downs filter by educator, and only once they have their
  // organization.
  if (!orgId) return null;

  if (isLoading) {
    return (
      <div className="w-full tablet:w-[280px]">
        <LoadingSelect />
      </div>
    );
  }

  if (mode === 'single') {
    return (
      <div className="w-full tablet:w-[280px]">
        <div className="flex w-full [&>div]:w-full [&_button]:min-w-0">
          <Select
            label="Teacher"
            value={selectedTeacherIds[0] ?? ''}
            disabled={disabled}
            onValueChange={(teacherId: string) => onChange([teacherId])}
          >
            {teacherOptions.map((teacher) => (
              <SelectItem key={teacher.value} value={teacher.value}>
                {teacher.label}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full tablet:w-[280px]">
      <LabeledMultiSelect
        label="Teachers"
        inputId="teacher-multiselect"
        selectAllLabel={ALL_TEACHERS_LABEL}
        options={teacherOptions}
        selectedIds={selectedTeacherIds}
        onChange={onChange}
      />
    </div>
  );
}
